import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, Send, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { STYLE_LABELS } from "@/lib/chatConstants";
import { MessageBubble } from "@/components/chat/MessageBubble";
import { StoryPanel } from "@/components/chat/StoryPanel";
import { UsageChip } from "@/components/chat/UsageChip";

export default function ChatView() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streamingId, setStreamingId] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [personas, setPersonas] = useState([]);
  const [pinnedIds, setPinnedIds] = useState([]);
  // Free-tier usage counters for showing "N/30 msgs today" chip in the header
  // + monthly recaps line in the Recap panel. `null` while loading or for Plus
  // users (they see no cap chips).
  const [usage, setUsage] = useState(null);
  const refetchUsage = useCallback(() => {
    if (user?.is_plus) { setUsage(null); return; }
    api.get("/subscription/status").then(({ data }) => {
      setUsage({
        messages_today: data.usage?.messages_today ?? 0,
        recaps_this_month: data.usage?.recaps_this_month ?? 0,
        messages_limit: data.free_limits?.messages_per_day ?? 30,
        recaps_limit: data.free_limits?.recaps_per_month ?? 5,
      });
    }).catch(() => {});
  }, [user?.is_plus]);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const inputRef = useRef(null);
  // Smooth-streaming buffer: server may push chunks in bursts; we drain to UI
  // a few characters per animation frame for a smooth typewriter feel.
  const streamBufferRef = useRef({ id: null, pending: "", raf: null, active: false });

  const loadChat = useCallback(() => {
    api.get(`/chats/${id}`).then(({ data }) => {
      setChat(data);
      setMessages(data.messages || []);
      setPinnedIds(data.pinned_message_ids || []);
    });
  }, [id]);

  useEffect(() => {
    loadChat();
    api.get("/personas").then(({ data }) => setPersonas(data.personas));
    refetchUsage();
    // Restore draft
    const draft = localStorage.getItem(`ica_draft_${id}`);
    if (draft) setInput(draft);
    return () => {
      if (streamBufferRef.current.raf) cancelAnimationFrame(streamBufferRef.current.raf);
    };
  }, [loadChat, id]);

  // Mobile-keyboard fix: when the on-screen keyboard opens, the viewport
  // shrinks. `100dvh` on the root handles most of it, but iOS Safari still
  // needs a nudge — we translate the sticky footer up by the keyboard's
  // occluded height so the input never disappears behind the keyboard.
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return;
    const vv = window.visualViewport;
    const applyOffset = () => {
      // How much of the layout viewport is hidden by the keyboard.
      const occluded = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      document.documentElement.style.setProperty("--kb-offset", `${occluded}px`);
    };
    applyOffset();
    vv.addEventListener("resize", applyOffset);
    vv.addEventListener("scroll", applyOffset);
    return () => {
      vv.removeEventListener("resize", applyOffset);
      vv.removeEventListener("scroll", applyOffset);
      document.documentElement.style.removeProperty("--kb-offset");
    };
  }, []);

  // Smooth streaming: drain buffered chars to the current streaming message
  const startDrain = useCallback((targetId) => {
    const buf = streamBufferRef.current;
    if (buf.active && buf.id === targetId) return;
    buf.id = targetId;
    buf.active = true;
    const tick = () => {
      const b = streamBufferRef.current;
      if (!b.active || !b.id) { b.raf = null; return; }
      if (b.pending.length) {
        // Reveal 2 chars per frame for a smoother feel; ramps up if very backed up.
        const step = Math.max(2, Math.ceil(b.pending.length / 30));
        const chunk = b.pending.slice(0, step);
        b.pending = b.pending.slice(step);
        setMessages((prev) => prev.map((m) => (m.id === b.id ? { ...m, content: (m.content || "") + chunk } : m)));
      }
      b.raf = requestAnimationFrame(tick);
    };
    if (buf.raf) cancelAnimationFrame(buf.raf);
    buf.raf = requestAnimationFrame(tick);
  }, []);

  const finishDrain = useCallback(async () => {
    // Wait for any buffered chars to flush before marking done
    return new Promise((resolve) => {
      const check = () => {
        const b = streamBufferRef.current;
        if (!b.pending.length) {
          b.active = false;
          if (b.raf) cancelAnimationFrame(b.raf);
          b.raf = null;
          b.id = null;
          resolve();
        } else {
          setTimeout(check, 30);
        }
      };
      check();
    });
  }, []);

  // Persist draft on every change (debounced by React batching)
  useEffect(() => {
    if (input) localStorage.setItem(`ica_draft_${id}`, input);
    else localStorage.removeItem(`ica_draft_${id}`);
  }, [input, id]);

  // Robust scroll-to-bottom: use a ResizeObserver on the inner message list
  // so we auto-scroll on ANY layout change (new message, streaming delta,
  // avatar image finally loaded, keyboard opening) — as long as the user
  // hasn't scrolled up on purpose. This survives the async image-load races
  // that make timer-based approaches unreliable on real mobile.
  const nearBottomRef = useRef(true);
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const THRESH = 140;
    const isNearBottom = () => (scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight) < THRESH;
    // Whenever the user scrolls, remember whether they were near the bottom.
    const onScroll = () => { nearBottomRef.current = isNearBottom(); };
    scroller.addEventListener("scroll", onScroll, { passive: true });

    // React to content-size changes (image load, streaming, edits, resends).
    const inner = scroller.firstElementChild;
    let ro;
    if (inner && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        // If any message is currently being edited (its textarea is in the
        // DOM), DON'T auto-scroll — the edit useEffect on MessageBubble is
        // responsible for the scroll position, and the extra `--edit-pad`
        // headroom would otherwise cause us to snap to a blank stretch of
        // padding, hiding the textarea behind the keyboard.
        if (scroller.querySelector('textarea[data-testid^="msg-edit-textarea-"]')) return;
        if (nearBottomRef.current) scroller.scrollTop = scroller.scrollHeight;
      });
      ro.observe(inner);
    }
    return () => { if (ro) ro.disconnect(); scroller.removeEventListener("scroll", onScroll); };
  }, [id, !!chat]);

  // On chat open (messages first arrive), force-park at bottom regardless of
  // where scrollTop currently is. This runs alongside the ResizeObserver above
  // — the observer handles growth over time; this handles the instant of first
  // paint when nearBottomRef hasn't been evaluated yet.
  useEffect(() => {
    if (messages.length === 0) return;
    const scroller = scrollRef.current;
    if (!scroller) return;
    nearBottomRef.current = true;
    scroller.scrollTop = scroller.scrollHeight;
  }, [id, messages.length]);

  async function send() {
    if (sending) return;
    const content = input.trim();
    // Empty send button →
    //   - Last message is USER: generate the bot's first reply (via /regenerate
    //     fresh-reply path — preserves rate-limits + variant flow).
    //   - Last message is ASSISTANT: keep going. Append a NEW assistant
    //     message via /continue (do NOT create a variant of the last one).
    if (!content) {
      if (messages.length === 0) return;
      const last = messages[messages.length - 1];
      if (last.role === "assistant") {
        continueChat();
      } else {
        regenerateLast();
      }
      return;
    }
    setInput("");
    localStorage.removeItem(`ica_draft_${id}`);
    // Reset the auto-grown textarea back to a single row after clearing.
    if (inputRef.current) inputRef.current.style.height = "auto";
    setSending(true);

    // Optimistically add user message
    const tempUserId = `temp-user-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: tempUserId, role: "user", content, created_at: new Date().toISOString() },
    ]);

    // Placeholder for assistant
    const tempAsstId = `temp-asst-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: tempAsstId, role: "assistant", content: "", created_at: new Date().toISOString() },
    ]);
    setStreamingId(tempAsstId);

    // SSE via fetch stream
    const token = localStorage.getItem("ica_token");
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await fetch(`${API_BASE}/chats/${id}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        credentials: "include",
        body: JSON.stringify({ content }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        const err = await res.text().catch(() => "");
        throw new Error(err || `HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantText = "";
      let finalId = tempAsstId;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const raw = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = raw.split("\n");
          let evt = "message";
          let dataStr = "";
          for (const l of lines) {
            if (l.startsWith("event:")) evt = l.slice(6).trim();
            else if (l.startsWith("data:")) dataStr += l.slice(5).trim();
          }
          if (!dataStr) continue;
          let payload;
          try { payload = JSON.parse(dataStr); } catch { continue; }

          if (evt === "delta") {
            assistantText += payload.text;
            // Push to smooth-streaming buffer; the UI drains a few chars per frame
            streamBufferRef.current.pending += payload.text;
            startDrain(finalId);
          } else if (evt === "assistant_start") {
            finalId = payload.id;
            setMessages((prev) =>
              prev.map((m) => (m.id === tempAsstId ? { ...m, id: payload.id } : m))
            );
            setStreamingId(payload.id);
          } else if (evt === "user_saved") {
            setMessages((prev) =>
              prev.map((m) => (m.id === tempUserId ? { ...m, id: payload.id } : m))
            );
          } else if (evt === "done") {
            await finishDrain();
            setStreamingId(null);
          } else if (evt === "error") {
            toast.error(payload.error || "Something went wrong.");
            setStreamingId(null);
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error(e.message || "Chat failed.");
      }
    } finally {
      setSending(false);
      setStreamingId(null);
      // Refresh cap counters after send/continue/regen finishes.
      refetchUsage();
    }
  }

  async function addMemory(text) {
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/chats/${id}/memory`, { content: text });
      setChat((c) => ({ ...c, pinned_memories: data.pinned_memories }));
      toast.success("Memory pinned.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed.");
    }
  }

  async function delMemory(i) {
    const { data } = await api.delete(`/chats/${id}/memory/${i}`);
    setChat((c) => ({ ...c, pinned_memories: data.pinned_memories }));
  }

  async function changeStyle(style) {
    try {
      await api.patch(`/chats/${id}`, { chat_style: style });
      setChat((c) => ({ ...c, chat_style: style }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  async function changeLength(length) {
    try {
      await api.patch(`/chats/${id}`, { response_length: length });
      setChat((c) => ({ ...c, response_length: length }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  async function changePersona(pid) {
    try {
      await api.patch(`/chats/${id}`, { persona_id: pid || "" });
      const persona = personas.find((p) => p.id === pid) || null;
      setChat((c) => ({ ...c, persona_id: pid || null, persona }));
      toast.success(persona ? `Persona: ${persona.name}` : "Persona cleared.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  async function changeModel(m) {
    // Admin-only dev toggle: pick between Claude (SFW default), Opus (pricier),
    // or DeepSeek V3 (uncensored, NSFW default).
    try {
      await api.patch(`/chats/${id}`, { llm_model: m });
      setChat((c) => ({ ...c, llm_model: m }));
      const labels = {
        "claude-opus-4-8": "Switched to Opus 4.8 (premium)",
        "deepseek-chat":   "Switched to DeepSeek V3 (uncensored)",
        "claude-sonnet-4-6": "Switched to Sonnet 4.6 (default)",
      };
      toast.success(labels[m] || "Model updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  async function deleteChat() {
    if (!confirm("Delete this chat? This cannot be undone.")) return;
    await api.delete(`/chats/${id}`);
    nav("/chats", { replace: true });
  }

  const [exporting, setExporting] = useState(false);
  async function exportChat(format = "pdf") {
    // Immersive+ (and admin) only. Downloads a stand-alone document you can
    // save, print, or share.
    if (!user?.is_plus && !user?.is_admin) {
      toast.error("Export is an Immersive+ perk.");
      nav("/upgrade");
      return;
    }
    setExporting(true);
    try {
      const res = await api.get(`/chats/${id}/export`, {
        params: { format },
        responseType: "blob",
      });
      const ext = format === "md" ? "md" : format;
      const mime = format === "pdf" ? "application/pdf"
                 : format === "html" ? "text/html;charset=utf-8"
                 : "text/markdown;charset=utf-8";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(chat?.character?.name || "chat").replace(/[^\w -]/g, "").slice(0, 40) || "chat"}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Chat exported.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed.");
    } finally {
      setExporting(false);
    }
  }

  async function saveEditedMessage(msgId, newContent) {
    try {
      await api.patch(`/chats/${id}/messages/${msgId}`, { content: newContent });
      setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, content: newContent, edited_at: new Date().toISOString() } : m)));
      toast.success("Message updated.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update.");
    }
  }

  async function continueChat() {
    // Append a NEW assistant message (the character keeps talking). Streams
    // into a fresh placeholder — different from regenerateLast which replaces
    // the last assistant reply in-place with a variant.
    if (sending) return;
    if (messages.length === 0) return;
    setSending(true);
    const tempId = `temp-cont-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: tempId, role: "assistant", content: "", created_at: new Date().toISOString() },
    ]);
    setStreamingId(tempId);

    const token = localStorage.getItem("ica_token");
    let gotAssistantStart = false;
    try {
      const res = await fetch(`${API_BASE}/chats/${id}/continue`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token ? `Bearer ${token}` : "" },
        credentials: "include",
      });
      if (!res.ok || !res.body) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalId = tempId;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const raw = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = raw.split("\n");
          let evt = "message"; let dataStr = "";
          for (const l of lines) {
            if (l.startsWith("event:")) evt = l.slice(6).trim();
            else if (l.startsWith("data:")) dataStr += l.slice(5).trim();
          }
          if (!dataStr) continue;
          let p; try { p = JSON.parse(dataStr); } catch { continue; }
          if (evt === "assistant_start") {
            gotAssistantStart = true;
            finalId = p.id;
            setMessages((prev) => prev.map((m) => (m.id === tempId ? { ...m, id: p.id } : m)));
            setStreamingId(p.id);
          } else if (evt === "delta") {
            streamBufferRef.current.pending += p.text;
            startDrain(finalId);
          } else if (evt === "done") {
            await finishDrain();
            setStreamingId(null);
          } else if (evt === "error") {
            toast.error(p.error);
            setStreamingId(null);
          }
        }
      }
    } catch (e) {
      toast.error(e.message || "Continue failed.");
    } finally {
      setSending(false);
      setStreamingId(null);
      // Clean up the orphan placeholder if we never received an id.
      if (!gotAssistantStart) {
        setMessages((prev) => prev.filter((m) => m.id !== tempId));
      }
      refetchUsage();
    }
  }

  async function regenerateLast() {
    if (sending) return;
    if (messages.length === 0) return;
    const last = messages[messages.length - 1];

    // Two modes:
    //  - Fresh-reply: last message is a USER turn. We append a NEW assistant
    //    placeholder and let the backend stream into it.
    //  - Regenerate: last is an assistant turn. Replace its content with a
    //    placeholder; the backend keeps the same id and appends a variant.
    const freshReply = last.role === "user";
    let realIdx;
    if (freshReply) {
      realIdx = messages.length; // insert at the end
    } else {
      // Regenerate — original constraint: can't regen the very first greeting.
      const lastAsstIndex = [...messages].reverse().findIndex((m) => m.role === "assistant");
      if (lastAsstIndex === -1) return;
      realIdx = messages.length - 1 - lastAsstIndex;
      const priorHasUser = messages.slice(0, realIdx).some((m) => m.role === "user");
      if (!priorHasUser) {
        toast.error("Cannot regenerate the character's greeting.");
        return;
      }
    }
    setSending(true);

    // Insert or replace with an empty placeholder.
    const tempId = `temp-regen-${Date.now()}`;
    setMessages((prev) => {
      const copy = [...prev];
      const placeholder = { id: tempId, role: "assistant", content: "", created_at: new Date().toISOString() };
      if (freshReply) copy.push(placeholder);
      else copy[realIdx] = placeholder;
      return copy;
    });
    setStreamingId(tempId);

    const token = localStorage.getItem("ica_token");
    let gotAssistantStart = false;
    try {
      const res = await fetch(`${API_BASE}/chats/${id}/regenerate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token ? `Bearer ${token}` : "" },
        credentials: "include",
      });
      if (!res.ok || !res.body) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantText = "";
      let finalId = tempId;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const raw = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = raw.split("\n");
          let evt = "message"; let dataStr = "";
          for (const l of lines) {
            if (l.startsWith("event:")) evt = l.slice(6).trim();
            else if (l.startsWith("data:")) dataStr += l.slice(5).trim();
          }
          if (!dataStr) continue;
          let p; try { p = JSON.parse(dataStr); } catch { continue; }
          if (evt === "assistant_start") {
            gotAssistantStart = true;
            finalId = p.id;
            setMessages((prev) => prev.map((m) => (m.id === tempId ? { ...m, id: p.id, content: "" } : m)));
            setStreamingId(p.id);
          } else if (evt === "delta") {
            assistantText += p.text;
            streamBufferRef.current.pending += p.text;
            startDrain(finalId);
          } else if (evt === "done") {
            await finishDrain();
            // Sync variants count/index so the < N/M > arrows appear immediately.
            if (typeof p.variants_count === "number" && typeof p.active_variant === "number") {
              setMessages((prev) => prev.map((m) => (
                m.id === finalId
                  ? {
                      ...m,
                      variants: Array.from({ length: p.variants_count }, (_, i) => (i === p.active_variant ? p.content : (m.variants && m.variants[i]) || "")),
                      active_variant: p.active_variant,
                      content: p.content,
                    }
                  : m
              )));
            }
            setStreamingId(null);
          } else if (evt === "error") {
            toast.error(p.error);
            setStreamingId(null);
          }
        }
      }
    } catch (e) {
      toast.error(e.message || "Regenerate failed.");
    } finally {
      setSending(false);
      setStreamingId(null);
      // If we opened a placeholder for the fresh-reply path but the stream
      // died before yielding assistant_start (no server id), remove the orphan
      // temp bubble so the UI doesn't leave a phantom.
      if (freshReply && !gotAssistantStart) {
        setMessages((prev) => prev.filter((m) => m.id !== tempId));
      }
    }
  }

  async function playRecap() {
    if (sending) return;
    try {
      toast.info("Generating recap…");
      const { data } = await api.post(`/chats/${id}/recap`);
      // Show the recap as a long-lived toast (no TTS playback — voice removed).
      toast(data.recap, { duration: 15000 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Recap failed.");
    }
  }

  async function togglePinMessage(msg) {
    const isPinned = pinnedIds.includes(msg.id);
    try {
      if (isPinned) {
        const { data } = await api.delete(`/chats/${id}/pin-message/${msg.id}`);
        setPinnedIds(data.pinned_message_ids);
        toast.success("Unpinned.");
      } else {
        const { data } = await api.post(`/chats/${id}/pin-message`, { message_id: msg.id });
        setPinnedIds(data.pinned_message_ids);
        toast.success("Pinned to memory.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Pin failed.");
    }
  }

  async function switchVariant(msgId, index) {
    try {
      const { data } = await api.patch(`/chats/${id}/messages/${msgId}/variant`, { index });
      setMessages((prev) => prev.map((m) => (
        m.id === msgId ? { ...m, content: data.content, active_variant: data.active_variant } : m
      )));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not switch variant.");
    }
  }

  async function deleteMessage(msgId) {
    if (!window.confirm("Delete this message? This can't be undone.")) return;
    try {
      await api.delete(`/chats/${id}/messages/${msgId}`);
      setMessages((prev) => prev.filter((m) => m.id !== msgId));
      setPinnedIds((prev) => prev.filter((pid) => pid !== msgId));
      toast.success("Message deleted.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed.");
    }
  }

  async function forkChatAt(msgId) {
    try {
      const { data } = await api.post(`/chats/${id}/messages/${msgId}/fork`);
      toast.success("New chat forked.");
      nav(`/chat/${data.chat.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not fork chat.");
    }
  }

  async function rewindTo(msgId) {
    if (!window.confirm("Rewind to this message? Every reply after it will be deleted.")) return;
    try {
      const { data } = await api.post(`/chats/${id}/messages/${msgId}/rewind`);
      // Cut local state after the pivot.
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === msgId);
        return idx === -1 ? prev : prev.slice(0, idx + 1);
      });
      setPinnedIds((prev) => prev.filter((pid) => prev.includes(pid)));
      toast.success(`Rewound — removed ${data.deleted} message(s).`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rewind failed.");
    }
  }

  if (!chat) return <div className="p-10 text-center text-neutral-500">Loading…</div>;

  const character = chat.character || {};

  return (
    <div className="relative flex h-[100dvh] flex-col overflow-hidden">
      {character.wallpaper_url && (
        <div
          data-testid="chat-wallpaper"
          aria-hidden
          className="pointer-events-none fixed inset-0 z-0"
          style={{
            backgroundImage: `url(${character.wallpaper_url})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
          }}
        >
          {/* Layered gradient overlay so text remains readable regardless of image */}
          <div className="absolute inset-0 bg-[#0B0A10]/70 backdrop-blur-[2px]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0A10]/60 via-transparent to-[#0B0A10]/80" />
        </div>
      )}
      <div className="relative z-10 flex h-[100dvh] flex-col overflow-hidden">
        {/* Header — fixed to the visual viewport so it never scrolls out on long chats */}
        <header className={`fixed top-0 left-0 right-0 z-30 flex items-center gap-3 border-b border-white/5 px-4 pt-safe pb-3 ${character.wallpaper_url ? "bg-black/40 backdrop-blur-xl" : "glass-strong"}`} data-testid="chat-header">
          <button data-testid="chat-back-btn" onClick={() => nav("/chats")} className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10">
            <ArrowLeft size={18} />
          </button>
          {character.avatar_url ? (
            <img src={character.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover" />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-500/20 font-display text-violet-200">
              {character.name?.[0]}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="font-display text-lg text-white truncate">{character.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              {STYLE_LABELS[chat.chat_style] || "Pick a style"}
              {character.is_nsfw && " · 18+"}
            </div>
          </div>
          {usage && (
            <UsageChip
              used={usage.messages_today}
              limit={usage.messages_limit}
              label="today"
              onClick={() => nav("/upgrade")}
            />
          )}
          <button
            data-testid="chat-panel-btn"
            onClick={() => setPanelOpen(true)}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10"
          >
            <Sparkles size={16} />
          </button>
        </header>

        {/* Messages scroller — pad top for the fixed header, pad bottom for the fixed composer PLUS the current keyboard offset so the last message can scroll all the way above the keyboard when editing. */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4"
          style={{
            paddingTop: "calc(76px + env(safe-area-inset-top))",
            paddingBottom: "calc(128px + env(safe-area-inset-bottom) + var(--kb-offset, 0px) + var(--edit-pad, 0px))",
          }}
          data-testid="chat-messages"
        >
          <div className="mx-auto max-w-2xl space-y-4">
            {messages.map((m, i) => {
              const isLastAssistant = m.role === "assistant" && i === messages.length - 1;
              // Allow editing the most recent assistant message AND the most recent user message
              let canEdit = false;
              if (m.role === "assistant" && isLastAssistant) canEdit = true;
              if (m.role === "user") {
                let lastUserIdx = -1;
                for (let j = messages.length - 1; j >= 0; j--) {
                  if (messages[j].role === "user") { lastUserIdx = j; break; }
                }
                if (i === lastUserIdx) canEdit = true;
              }
              return (
                <MessageBubble
                  key={m.id}
                  message={m}
                  character={character}
                  streaming={m.id === streamingId}
                  onEdit={canEdit ? (text) => saveEditedMessage(m.id, text) : null}
                  onRegenerate={isLastAssistant && !sending ? regenerateLast : null}
                  onTogglePin={() => togglePinMessage(m)}
                  onSwitchVariant={switchVariant}
                  onDelete={() => deleteMessage(m.id)}
                  onFork={() => forkChatAt(m.id)}
                  onRewind={() => rewindTo(m.id)}
                  pinned={pinnedIds.includes(m.id)}
                />
              );
            })}
            {sending && !streamingId && (
              <div className="flex items-center gap-1.5 pl-2">
                <span className="typing-dot h-1.5 w-1.5 rounded-full bg-neutral-400" />
                <span className="typing-dot h-1.5 w-1.5 rounded-full bg-neutral-400" />
                <span className="typing-dot h-1.5 w-1.5 rounded-full bg-neutral-400" />
              </div>
            )}
          </div>
        </div>

        <footer
          data-testid="chat-composer"
          className="glass-strong fixed bottom-0 left-0 right-0 z-30 border-t border-white/5 px-4 pt-3 pb-safe-4 transition-transform"
          style={{ transform: "translateY(calc(var(--kb-offset, 0px) * -1))" }}
        >
          <div className="mx-auto flex max-w-2xl items-end gap-2">
            <textarea
              ref={inputRef}
              data-testid="chat-input"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                // Auto-grow: reset then set to scrollHeight so the box expands
                // with the message. CSS max-h-40 caps growth and enables scroll.
                const el = e.target;
                el.style.height = "auto";
                el.style.height = Math.min(el.scrollHeight, 160) + "px";
              }}
              onFocus={() => {
                // Nudge the latest message into view once. `interactive-widget=resizes-content`
                // + the --kb-offset transform on the fixed footer handle everything else,
                // so we don't fight the browser's own layout on every keystroke.
                const scroller = scrollRef.current;
                if (!scroller) return;
                setTimeout(() => {
                  scroller.scrollTop = scroller.scrollHeight;
                }, 250);
              }}
              placeholder={`Message ${character.name}…`}
              rows={1}
              className="min-h-[44px] max-h-40 flex-1 resize-none rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 text-white outline-none focus:border-violet-500/50 overflow-y-auto"
            />
            <button
              data-testid="chat-send-btn"
              onClick={send}
              disabled={sending || (!input.trim() && messages.length === 0)}
              title={input.trim() ? "Send" : (messages.length ? "Ask the bot to reply again" : "Send")}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white shadow-lg shadow-violet-500/30 disabled:opacity-40"
            >
              {input.trim() ? <Send size={16} /> : <RefreshCw size={16} />}
            </button>
          </div>
          <p className="mx-auto max-w-2xl mt-1.5 text-[10px] text-center text-neutral-600">
            This is A.I. Treat everything it says as fiction.
          </p>
        </footer>

        <StoryPanel
          open={panelOpen}
          onOpenChange={setPanelOpen}
          chat={chat}
          chatId={id}
          personas={personas}
          pinnedIds={pinnedIds}
          setPinnedIds={setPinnedIds}
          changeStyle={changeStyle}
          changeLength={changeLength}
          changeModel={changeModel}
          changePersona={changePersona}
          addMemory={addMemory}
          delMemory={delMemory}
          playRecap={playRecap}
          usage={usage}
          exporting={exporting}
          exportChat={exportChat}
          deleteChat={deleteChat}
          user={user}
        />
      </div>
    </div>
  );
}
